import { InjectionToken, ModuleWithProviders, NgModule } from '@angular/core';
import { AiKnowledgeConfig } from './interface/config.interface';
import { AiKnowledgeService } from './ai-knowledge.service';
import {
  AI_KNOWLEDGE_CONFIG_TOKEN,
  ACCOUNT_GROUP_ID_TOKEN,
  MARKET_ID_TOKEN,
  PARTNER_ID_TOKEN,
  MANAGE_KNOWLEDGE_URL_TOKEN,
  BUSINESS_PROFILE_URL_TOKEN,
  SHOW_BUSINESS_PROFILE_SOURCE_TOKEN,
} from './tokens';
import { isObservable, Observable, of } from 'rxjs';
import { ApplicationKnowledgeComponent } from './application-knowledge-control/application-knowledge.component';
import { AppsForKnowledgeSourceComponent } from './apps-for-knowledge-source/apps-for-knowledge-source.component';
import { CustomKnowledgeEditorComponent } from './custom-knowledge-editor/custom-knowledge-editor.component';
import { DeleteCustomKnowledgeComponent } from './delete-custom-knowledge/delete-custom-knowledge.component';
import { KnowledgeListComponent } from './knowledge-list/knowledge-list.component';
import { KnowledgeSourceManagementComponent } from './knowledge-source-management/knowledge-source-management.component';
import { LearnMoreBusinessProfileComponent } from './learn-more-business-profile/learn-more-business-profile.component';

@NgModule({
  imports: [
    ApplicationKnowledgeComponent,
    AppsForKnowledgeSourceComponent,
    CustomKnowledgeEditorComponent,
    DeleteCustomKnowledgeComponent,
    KnowledgeListComponent,
    KnowledgeSourceManagementComponent,
    LearnMoreBusinessProfileComponent,
  ],
})
export class AiKnowledgeModule {
  static forRoot(c: { config: InjectionToken<AiKnowledgeConfig> }): ModuleWithProviders<AiKnowledgeModule> {
    const result = {
      ngModule: AiKnowledgeModule,
      providers: [
        AiKnowledgeService,
        { provide: AI_KNOWLEDGE_CONFIG_TOKEN, useExisting: c.config },
        { provide: ACCOUNT_GROUP_ID_TOKEN, deps: [AI_KNOWLEDGE_CONFIG_TOKEN], useFactory: accountGroupIdFactory },
        { provide: MARKET_ID_TOKEN, deps: [AI_KNOWLEDGE_CONFIG_TOKEN], useFactory: marketIdFactory },
        { provide: PARTNER_ID_TOKEN, deps: [AI_KNOWLEDGE_CONFIG_TOKEN], useFactory: partnerIdFactory },
        {
          provide: MANAGE_KNOWLEDGE_URL_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: manageKnowledgeUrlFactory,
        },
        {
          provide: BUSINESS_PROFILE_URL_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: businessProfileUrlFactory,
        },
        {
          provide: SHOW_BUSINESS_PROFILE_SOURCE_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: showBusinessProfileSourceFactory,
        },
      ],
    };
    return result;
  }
}

function accountGroupIdFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.accountGroupId$) ? config.accountGroupId$ : of(config.accountGroupId$);
}

function marketIdFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.marketId$) ? config.marketId$ : of(config.marketId$);
}

function partnerIdFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.partnerId$) ? config.partnerId$ : of(config.partnerId$);
}

function manageKnowledgeUrlFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.manageKnowledgeUrl$) ? config.manageKnowledgeUrl$ : of(config.manageKnowledgeUrl$);
}

function businessProfileUrlFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.businessProfileUrl$) ? config.businessProfileUrl$ : of(config.businessProfileUrl$);
}

function showBusinessProfileSourceFactory(config: AiKnowledgeConfig): Observable<boolean | undefined | null> {
  return isObservable(config.showBusinessProfileSource$)
    ? config.showBusinessProfileSource$
    : of(config.showBusinessProfileSource$);
}
